import { CommonModule } from '@angular/common';
import { Component, ContentChild, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { GenericTagComponent } from '@shared/components/generic-tag/generic-tag.component';
import { GenericFilterToolbarComponent } from '@shared/components/generic-filter-toolbar/generic-filter-toolbar.component';
import { PageConstant } from '@shared/constants';
import { ISort, PaginationRequest, SORT_VAL, AuthService } from '@shared/services';
import { ApiCallService } from '@shared/services/api-call.service';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';
import {
    TableConfig,
    TableDataLoadEvent,
    TableLazyLoadParams
} from './generic-p-table.interfaces';

@Component({
    selector: 'generic-p-table',
    standalone: true,
    imports: [
        CommonModule,
        TableModule,
        ButtonModule,
        ToolbarModule,
        TooltipModule,
        ConfirmDialogModule,
        GenericTagComponent,
        GenericFilterToolbarComponent,
    ],
    providers: [ConfirmationService],
    templateUrl: './generic-p-table.component.html',
    styleUrls: ['./generic-p-table.component.scss'],
    host: {
        'class': '!flex flex-col flex-1 min-h-0 min-w-0 w-full h-full overflow-auto'
    }
})
export class GenericPTableComponent<T = any> implements OnInit, OnChanges, OnDestroy {
    @Input() config!: TableConfig<T>;
    @Input() data: T[] = [];
    @Input() totalRecords: number = 0;
    @Input() selection: T | T[] | null = null;
    @Input() initialData: T[] = []; // Data to use when useApiData is false
    @Input() dependencyValue: any = null; // External dependency value (e.g., departmentId from parent component)
    @Input() internalFilter: any = {}; // Filter from internal toolbar (e.g., GenericFilterToolbarComponent)

    @Output() onDataLoad = new EventEmitter<TableDataLoadEvent>();
    @Output() onLazyLoad = new EventEmitter<TableLazyLoadParams>();
    @Output() selectionChange = new EventEmitter<T | T[] | null>();

    // Internal loading state
    loading: boolean = false;

    // Filter state management
    private originalBaseQuery: any = {}; // Preserve original config.baseQuery
    private externalFilter: any = {}; // Filter from external dependency
    private currentPathParameter: string | null = null; // Current path parameter value

    // Content projection for custom templates
    @ContentChild('customActions') customActionsTemplate?: TemplateRef<any>;
    @ContentChild('toolbarActions') toolbarActionsTemplate?: TemplateRef<any>;
    @ContentChild('customColumnTemplate') customColumnTemplate?: TemplateRef<any>;

    @ViewChild('dataTable') dataTable!: Table;

    // Internal state
    private destroy$ = new Subject<void>();

    // Pagination and sorting state
    itemPerPage: number = PageConstant.ITEM_PER_PAGE;
    itemPerPageOptions: number[] = PageConstant.ITEM_PER_PAGE_OPTIONS;
    offset: number = 0;
    sort: ISort = { field: null, order: 1 };

    constructor(
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private apiCallService: ApiCallService
    ) { }

    ngOnInit() {
        if (!this.config) {
            throw new Error('LazyscrollPTableComponent requires a config input');
        }

        // Validate configuration based on data source type
        const useApiData = this.config.useApiData !== false; // Default to true
        if (useApiData && (!this.config.service || !this.config.method)) {
            throw new Error('service and method are required when useApiData is true');
        }

        // Preserve original baseQuery from config
        this.originalBaseQuery = this.config.baseQuery ? { ...this.config.baseQuery } : {};

        // Set default values from config
        if (this.config.itemsPerPage) {
            this.itemPerPage = this.config.itemsPerPage;
        }
        if (this.config.itemsPerPageOptions) {
            this.itemPerPageOptions = this.config.itemsPerPageOptions;
        }

        // Handle initial dependency value for API mode
        if (useApiData && this.dependencyValue !== null) {
            if (this.config.usePathParameter && this.config.pathParameterExtractor) {
                this.currentPathParameter = this.config.pathParameterExtractor(this.dependencyValue);
            } else {
            }
            if (this.config.onDependencyChange) {
                this.externalFilter = this.config.onDependencyChange(this.dependencyValue) || {};
            }
        }

        // Initialize data based on configuration
        if (!useApiData) {
            this.initializeVariableData();
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // Handle changes to initialData when using variable data
        const useApiData = this.config?.useApiData !== false;
        if (!useApiData && changes['initialData'] && !changes['initialData'].firstChange) {
            this.initializeVariableData();
            return;
        }

        // Handle internalFilter changes for API mode only
        if (changes['internalFilter'] && !changes['internalFilter'].firstChange && useApiData) {
            // Reload data with new internal filter
            this.reload();
            return;
        }

        // Handle dependency value changes for API mode only
        if (changes['dependencyValue'] && !changes['dependencyValue'].firstChange && useApiData) {
            const currentValue = changes['dependencyValue'].currentValue;
            const previousValue = changes['dependencyValue'].previousValue;

            // Only process if the value actually changed
            if (currentValue !== previousValue) {

                // Update path parameter if using path parameter mode
                if (this.config.usePathParameter && this.config.pathParameterExtractor) {
                    this.currentPathParameter = this.config.pathParameterExtractor(currentValue);
                }

                // Update external filter using onDependencyChange callback if available
                if (this.config.onDependencyChange) {
                    this.externalFilter = this.config.onDependencyChange(currentValue) || {};
                } else {
                    // Clear external filter if no callback provided
                    this.externalFilter = {};
                }

                // Reload data with new filter
                this.reload();
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onFiltersChanged(filters: any) {
        this.internalFilter = filters;
        this.reload();
    }

    private initializeVariableData() {
        // When using variable data, set up the data and total records
        this.data = this.initialData || [];
        this.totalRecords = this.data.length;
        this.loading = false;

        // Emit data load event for consistency
        this.onDataLoad.emit({
            items: this.data,
            totalRecords: this.totalRecords,
            loading: this.loading
        });
    }

    private handleVariableDataLazyLoad(event: TableLazyLoadEvent) {
        // For variable data, we handle sorting and pagination locally
        let processedData = [...(this.initialData || [])];

        // Handle sorting
        if (event.sortField && event.sortOrder) {
            processedData.sort((a: any, b: any) => {
                const aVal = this.getNestedProperty(a, event.sortField as string);
                const bVal = this.getNestedProperty(b, event.sortField as string);

                if (aVal < bVal) return event.sortOrder === 1 ? -1 : 1;
                if (aVal > bVal) return event.sortOrder === 1 ? 1 : -1;
                return 0;
            });
        }

        // Handle pagination
        const first = event.first || 0;
        const rows = event.rows || this.itemPerPage;
        const paginatedData = processedData.slice(first, first + rows);

        // Update component state
        this.data = paginatedData;
        this.totalRecords = processedData.length;
        this.offset = first;
        this.itemPerPage = rows;
        this.sort = {
            field: event.sortField,
            order: event.sortOrder || 1
        };

        // Emit events
        this.onLazyLoad.emit({
            first: first,
            rows: rows,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        });

        this.onDataLoad.emit({
            items: paginatedData,
            totalRecords: this.totalRecords,
            loading: false
        });
    }

    private getNestedProperty(obj: any, path: string): any {
        return path.split('.').reduce((current, prop) => current?.[prop], obj);
    }

    onTableLazyLoad(event: TableLazyLoadEvent) {
        const useApiData = this.config.useApiData !== false; // Default to true

        // If using variable data, handle locally
        if (!useApiData) {
            this.handleVariableDataLazyLoad(event);
            return;
        }

        const newOffset = event.first || 0;
        const newItemPerPage = event.rows || this.itemPerPage;
        const newSort = {
            field: event.sortField,
            order: event.sortOrder || 1
        };

        // Check if this is a sort/filter change (reset data) or just pagination (append data)
        const isSortChange = this.sort.field !== newSort.field || this.sort.order !== newSort.order;
        const isPageSizeChange = this.itemPerPage !== newItemPerPage;
        const resetData = isSortChange || isPageSizeChange || newOffset === 0;

        // Check for gaps when not resetting data (virtual scroll gap detection)
        if (!resetData && this.config.features?.virtualScroll) {
            const gap = newOffset - this.offset;
            if (gap > this.itemPerPage) {
                // Request the missing in-between page first
                console.log('Gap detected, fetching missing page...', this.offset + '-' + newOffset);
                const missingOffset = this.offset + this.itemPerPage;
                this.loadData({
                    first: missingOffset,
                    rows: this.itemPerPage,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder
                }, false);
            }
        }

        this.offset = newOffset;
        // Only update itemPerPage if not using virtual scroll or if it's a legitimate page size change
        // Virtual scroll may adjust event.rows based on viewport, but we should preserve configured page size
        if (!this.config.features?.virtualScroll) {
            this.itemPerPage = newItemPerPage;
        }
        this.sort = newSort;

        // Emit the lazy load event for parent component to handle if needed
        this.onLazyLoad.emit({
            first: newOffset,
            rows: newItemPerPage,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        });

        // Load data
        this.loadData({
            first: newOffset,
            rows: newItemPerPage,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }, resetData, event);
    }

    private loadData(params: TableLazyLoadParams, resetData: boolean = false, event?: TableLazyLoadEvent) {
        this.loading = true;

        // Build query and body based on configuration
        let query = {};
        let body = null;

        if (this.config.usePostMethod && this.config.bodyBuilder) {
            // Use enhanced body builder for POST requests with separate filter layers
            // TODO: Add search term support when global search feature is implemented
            const searchTerm = ''; // Future: get from search input
            body = this.config.bodyBuilder(params, this.originalBaseQuery, this.externalFilter, this.internalFilter, searchTerm);
        } else {
            // For GET requests, merge baseQuery, externalFilter and internalFilter into a single query object
            const combinedFilter = {
                ...(this.originalBaseQuery || {}),
                ...(this.externalFilter || {}),
                ...(this.internalFilter || {}),
            };

            if (this.config.queryBuilder) {
                // Allow custom query builder to transform/override the combined filter
                // Pass through paging/sort params plus filter context
                const builderParams: any = {
                    ...params,
                    baseQuery: this.originalBaseQuery,
                    externalFilter: this.externalFilter,
                    internalFilter: this.internalFilter,
                    filter: combinedFilter,
                };
                query = this.config.queryBuilder(builderParams) || combinedFilter;
            } else {
                query = combinedFilter;
            }
        }

        // Build request parameters
        const requestParams: PaginationRequest = {
            limit: params.rows,
            offset: params.first,
            sort: params.sortField ? `${params.sortField},${SORT_VAL[params.sortOrder?.toString() || '1']}` : '',
            query: this.config.usePostMethod ? null : query,
            ...this.config.additionalParams
        };

        // Call the service method dynamically
        if (!this.config.service || !this.config.method) {
            console.error('Service and method are required for API data loading');
            this.loading = false;
            return;
        }

        const serviceMethod = this.config.service[this.config.method];
        if (!serviceMethod) {
            console.error(`Method ${this.config.method} not found on service`);
            this.loading = false;
            return;
        }

        // Call service method with or without body based on configuration
        let serviceCall;
        if (this.config.usePostMethod) {
            if (this.config.usePathParameter) {
                if (!this.currentPathParameter) {
                    console.error('Path parameter is required but not provided');
                    this.loading = false;
                    return;
                }
                // Call with path parameter: method(pathParam, requestParams, body)
                serviceCall = serviceMethod.call(this.config.service, this.currentPathParameter, requestParams, body);
            } else {
                // Standard POST call: method(requestParams, body)
                serviceCall = serviceMethod.call(this.config.service, requestParams, body);
            }
        } else {
            // GET call: method(requestParams) or method(pathParam, requestParams) for path parameter support
            if (this.config.usePathParameter) {
                if (!this.currentPathParameter) {
                    console.error('Path parameter is required but not provided');
                    this.loading = false;
                    return;
                }
                // Call with path parameter: method(pathParam, requestParams)
                serviceCall = serviceMethod.call(this.config.service, this.currentPathParameter, requestParams);
            } else {
                // Standard GET call: method(requestParams)
                serviceCall = serviceMethod.call(this.config.service, requestParams);
            }
        }

        const serviceCall$ = (this.config.transformResponse
            ? serviceCall.pipe(map((resp: any) => this.config.transformResponse!(resp)))
            : serviceCall
        ).pipe(takeUntil(this.destroy$));

        this.apiCallService.fetch({
            serviceCall$: serviceCall$,
            onSuccess: (data: any) => {
                if (resetData) {
                    this.totalRecords = data.total;
                    this.data = this.config.features?.virtualScroll ? Array(data.total) : data.items || [];
                }

                if (this.config.features?.virtualScroll) {
                    const targetOffset = params.first;
                    const targetLimit = params.rows;
                    this.data.splice(targetOffset, targetLimit, ...(data.items || []));

                    if (event?.forceUpdate) {
                        event.forceUpdate();
                    }
                } else {
                    this.data = data.items || [];
                    this.totalRecords = data.total;
                }

                this.onDataLoad.emit({
                    items: this.data,
                    totalRecords: this.totalRecords,
                    loading: false
                });
            },
            onError: () => {
                this.onDataLoad.emit({
                    items: this.data,
                    totalRecords: this.totalRecords,
                    loading: false
                });
            },
            finalize: () => { this.loading = false; }
        });
    }

    // Action handlers - now execute embedded functions directly
    executeAction(action: any, item: T | undefined = undefined, event?: Event) {
        if (action.confirmMessage) {
            this.confirmationService.confirm({
                message: action.confirmMessage,
                header: 'Xác nhận',
                icon: 'pi pi-exclamation-triangle',
                accept: () => {
                    const result = action.onClick(item, event);
                    if (result instanceof Promise) {
                        result.catch(error => {
                            console.error('Action failed:', error);
                        });
                    }
                }
            });
        } else {
            const result = action.onClick(item, event);
            if (result instanceof Promise) {
                result.catch(error => {
                    console.error('Action failed:', error);
                });
            }
        }
    }

    // Utility methods
    getFieldValue(item: any, field: string): any {
        return field.split('.').reduce((obj, key) => obj?.[key], item);
    }

    get hasActions(): boolean {
        return !!(this.getRowActions().length || this.customActionsTemplate);
    }

    get hasToolbarActions(): boolean {
        return !!this.getToolbarActions().length;
    }

    getToolbarActions(): any[] {
        return this.config.actions?.filter(action =>
            action.location === 'toolbar' &&
            (!action.permission || this.authService.hasPermission(action.permission))
        ) || [];
    }

    getRowActions(): any[] {
        return this.config.actions?.filter(action =>
            action.location === 'row' &&
            (!action.permission || this.authService.hasPermission(action.permission))
        ) || [];
    }

    get virtualScrollItemSize(): number {
        return this.config.virtualScrollItemSize || 46.75;
    }

    get virtualScrollOptions(): any {
        return this.config.virtualScrollOptions || { numToleratedItems: 5 };
    }

    // Public methods
    reload() {
        this.onTableLazyLoad({
            first: 0,
            rows: this.itemPerPage,
            sortField: this.sort.field,
            sortOrder: this.sort.order
        });
    }

    refresh() {
        this.onTableLazyLoad({
            first: this.offset,
            rows: this.itemPerPage,
            sortField: this.sort.field,
            sortOrder: this.sort.order
        });
    }

    // Selection event handlers
    onTableRowSelect(event: any) {
        const selectedItem = event.data as T;
        this.selectionChange.emit(this.selection);

        if (this.config.features?.selection?.onSelect) {
            this.config.features.selection.onSelect(selectedItem, event.originalEvent);
        }
    }

    onTableRowUnselect(event: any) {
        const unselectedItem = event.data as T;
        this.selectionChange.emit(this.selection);

        if (this.config.features?.selection?.onUnselect) {
            this.config.features.selection.onUnselect(unselectedItem, event.originalEvent);
        }
    }

    // Selection configuration getters
    get selectionMode(): "single" | "multiple" | null | undefined {
        const mode = this.config.features?.selection?.mode;
        return mode === 'none' ? null : mode;
    }

    get metaKeySelection(): boolean {
        return this.config.features?.selection?.metaKeySelection ?? false;
    }

    get dataKey(): string {
        return this.config.features?.selection?.dataKey || 'id';
    }

    get hasSelection(): boolean {
        return this.config.features?.selection?.mode !== 'none' &&
            this.config.features?.selection?.mode !== undefined;
    }

    defaultTrackByFn(index: number, item: any): any {
        return item ? item.id : index;
    }
}
